/**
 * Delega - SPECIFICHE 2.4
 *
 * Deleghe sindacali, collegate al lavoratore.
 *
 * Stati (SPECIFICHE 4.1 - campo esplicito `stato`):
 * - BOZZA: appena creata, modificabile
 * - DA_PRESENTARE: pronta per la presentazione alla cassa
 * - PRESENTATA: inviata alla cassa, in attesa di riscontro
 * - CONFERMATA: riscontro positivo dalla cassa
 * - ARCHIVIATA: non piu' attiva (con motivo)
 */
import { z } from 'zod';
import { CassaCodeSchema, str, num, dateStr } from './common.ts';
import { ModalitaRaccoltaSchema } from './raccolta-delega.ts';

// Stati delega
export const STATI_DELEGA = ['BOZZA', 'DA_PRESENTARE', 'PRESENTATA', 'CONFERMATA', 'ARCHIVIATA'] as const;
export const StatoDelegaSchema = z.enum(STATI_DELEGA);
export type StatoDelega = z.infer<typeof StatoDelegaSchema>;

/** Etichette UI degli stati delega: i valori DB restano invariati (capitolato ottobre 2026, cap. 3). */
export const ETICHETTA_STATO_DELEGA: Record<StatoDelega, string> = {
  BOZZA: 'Cessati/Trasferiti',
  DA_PRESENTARE: 'Valutazione',
  PRESENTATA: 'Presentata',
  CONFERMATA: 'Confermata',
  ARCHIVIATA: 'Archiviata',
};

/** Etichetta di uno stato delega; per altri codici (motivi, modalità) formattazione generica "ABC_DEF" → "Abc def". */
export function etichettaStato(v: string): string {
  return ETICHETTA_STATO_DELEGA[v as StatoDelega]
    ?? v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

// Motivi archiviazione
export const MOTIVI_ARCHIVIAZIONE = ['RIFIUTATA', 'CESSATO', 'DIMESSO', 'ALTRO'] as const;
export const MotivoArchiviazioneSchema = z.enum(MOTIVI_ARCHIVIAZIONE);
export type MotivoArchiviazione = z.infer<typeof MotivoArchiviazioneSchema>;

export const DelegaSchema = z.object({
  id: z.number(),
  lavoratoreId: z.number(),
  cassa: CassaCodeSchema,
  stato: StatoDelegaSchema.default('DA_PRESENTARE'),
  dataPresentazione: dateStr,
  dataDecorrenza: dateStr,
  dataCessazione: dateStr,
  dataConferma: dateStr,
  dataArchiviazione: dateStr,
  motivoArchiviazione: MotivoArchiviazioneSchema.nullable().default(null),
  operatore: str,
  note: str,
  daArchivio: z.number().default(0),
  // Capitolato ottobre 2026, cap. 1: colore (lista `colori`) e modalità di raccolta
  coloreId: num.default(null),
  modalitaRaccolta: ModalitaRaccoltaSchema.nullable().default(null),
  // Invio al web service Statini (Fenice): istante e codice batch.
  // Codice null su delega accettata come duplicato da Statini.
  statiniInviataAt: str.default(null),
  statiniCodiceInserimento: str.default(null),
  createdAt: str,
  updatedAt: str,
});

export type Delega = z.infer<typeof DelegaSchema>;

// Per INSERT (senza id e timestamp). I campi Statini sono scritti solo
// dall'invio, mai in creazione.
export const DelegaInsertSchema = DelegaSchema.omit({
  id: true,
  statiniInviataAt: true,
  statiniCodiceInserimento: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  stato: StatoDelegaSchema.default('DA_PRESENTARE'),
});

export type DelegaInsert = z.infer<typeof DelegaInsertSchema>;
